var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Store from "@ember-data/store";
import { assert } from "@ember/debug";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import Medic from "core/models/medic";
import Patient from "core/models/patient";
import { removeItemFromLocalStorage } from "core/utils/use-local-storage";
import Ember from "ember";
import SessionService from "ember-simple-auth/services/session";
import ProSanteConnect from "./pro-sante-connect";

/**
 * A subclass of the Ember Simple Auth session service.
 *
 * Ember Simple Auth provides a mechanism to  make sure a user is authenticated
 * before entering some routes, and the logic for authenticating a user.
 *
 * This subclass adds a step in the authentication process: the retrieval of the
 * authenticated medic or patient object, which contains useful information used
 * throughout the app.
 *
 * This subclass also overrides the default behaviour of the `authenticate` and
 * `handleAuthentication` methods. If the authentication is successful,
 * `authenticate` emits an event that is captured by the session service itself
 * to run a transition to the logged in route, though these events are considered
 * obsolete.
 * This has a drawback: we cannot make sure that the user is loaded before
 * entering authenticated routes.
 * When the event is emitted, it actually calls `handleAuthentication`. This subclass
 * overrides the method so that it does nothing if the medic or patient is not retrieved,
 * and an explicit call to this method is done in `authenticate` after the user is retrieved.
 * We thus have the expected behaviour on our side while still being able to use Ember Simple Auth
 * as expected.
 */
let SemeiaSessionService = (_class = class SemeiaSessionService extends SessionService {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "proSanteConnect", _descriptor2, this);
    _initializerDefineProperty(this, "patient", _descriptor3, this);
    _initializerDefineProperty(this, "medic", _descriptor4, this);
    _initializerDefineProperty(this, "authenticatedAtThisSession", _descriptor5, this);
    /**
     * This is a temporary storage of an access token.
     * It is used while authenticating (not restoring a session): when authenticating,
     * the token is retrieved, then a call to fetchAuthenticatedUser is made.
     * But the whole authentication processed is not finished, thus this.session.authenticated is empty.
     * This attribute is used in the application adapter for authenticated requests.
     * `access_token` is used as a temporary storage, used instead of this.session.authenticated if present.
     */
    _defineProperty(this, "access_token", void 0);
  }
  handleInvalidation(routeAfterInvalidation) {
    super.handleInvalidation(routeAfterInvalidation);
    this.medic = undefined;
    this.patient = undefined;
    this.authenticatedAtThisSession = false;
    removeItemFromLocalStorage(this._selectedFilterKey);
  }

  /**
   * Utility method for fetching an authenticated user.
   */
  async fetchAuthenticatedUser(data) {
    assert("data.as must be patient or medic", ["patient", "medic"].includes(data.as));
    // This assert is to ensure we don't see https://semeia.sentry.io/issues/5743948091 again.
    assert("data.id is set", data.id);
    this.access_token = data.access_token;
    const authenticatedAs = data.as;
    if (authenticatedAs === "medic") {
      this.medic = await this.store.findRecord("medic", data.id, {
        include: ["health-centres.dmp-health-centre-config", "health-centres.health-centre-tags"].join(",")
      });
    } else if (authenticatedAs === "patient") {
      this.patient = await this.store.findRecord("patient", data.id, {
        include: "health-centre"
      });
    }
  }
  get logoutUrl() {
    return this.data.authenticated?.psc_id_token ? this.proSanteConnect.logoutUrl : "/medics-web/logout";
  }
  get user() {
    return this.medic ?? this.patient;
  }
  get productName() {
    if (this.user instanceof Patient) {
      return this.user?.healthCentre.productNameCapitalized ?? "Wise";
    }
    if (this.user instanceof Medic) {
      return this.user?.primaryHealthCenter?.productNameCapitalized ?? "Wise";
    }
    return "Wise";
  }
  get _selectedFilterKey() {
    const key = "medics-web.protected.updates.selectedFilter";
    if (Ember.testing) {
      return `test-${key}`;
    }
    return key;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "proSanteConnect", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "patient", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "medic", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "authenticatedAtThisSession", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _class);
export { SemeiaSessionService as default };